import React from 'react';
import classNames from 'classnames';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { assemblyDisplayName } from 'common/components/kit-shared/assemblySummary';
import type { IAssembly } from 'containers/KitWorkspaceStorage/types';

import styles from './AssemblyName.module.scss';

interface IAssemblyNameProps {
  assembly: IAssembly;
  // Applied to the named span so each context keeps its own font weight/size.
  nameClassName?: string;
  // Hide the "name recommended" warning badge (e.g. inside a kit line, where the
  // assembly is already placed and the nudge is just noise).
  hideRecommended?: boolean;
}

/**
 * Renders the assembly's name, or "Untitled assembly" plus a "name recommended"
 * warning badge when it has none. Shared by every list that shows assemblies
 * (standalone list, kit lines, review popup) so the untitled treatment is
 * consistent.
 */
const AssemblyName = ({ assembly, nameClassName, hideRecommended = false }: IAssemblyNameProps) => {
  const t = useKitTranslate();
  const name = assemblyDisplayName(assembly);

  if (name) {
    return <span className={nameClassName}>{name}</span>;
  }

  return (
    <span className={classNames(styles.untitledWrap, nameClassName)}>
      <span className={styles.untitled}>{t('texts.kits.untitledName')}</span>
      {!hideRecommended && (
        <span className={styles.badge}>{`⚠ ${t('texts.kits.nameRecommended')}`}</span>
      )}
    </span>
  );
};

export default AssemblyName;
